<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import { ArrowLeft, ExternalLink, Mail, MapPin, Phone } from 'lucide-vue-next'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import PortalLayout from '@/layouts/PortalLayout.vue'
import { APPLICATION_STATUSES } from '@/lib/constants'
import { formatLabel } from '@/lib/utils'
import { portalOrganizationRootPath } from '@/routes'

interface Address {
  unit_no: string | null
  street_no: string
  street_name: string
  street_type: string
  city: string
  state: string
  postcode: string
}

interface Partner {
  id: number
  name: string
  contact: string
  email: string
  phone: string
  active: boolean
  website: string | null
  created_at: string
  address: Address | null
  app_counts: Record<string, number>
}

const props = defineProps<{ partner: Partner }>()

function formatAddress(a: Address): string {
  const street = [a.unit_no, a.street_no, a.street_name, a.street_type].filter(Boolean).join(' ')
  return [street, a.city, a.state, a.postcode].filter(Boolean).join(', ')
}

const totalApps = Object.values(props.partner.app_counts).reduce((s, n) => s + n, 0)

const statusColorMap: Record<string, string> = {
  new:      'bg-blue-50 text-blue-700 border-blue-200',
  taken:    'bg-yellow-50 text-yellow-700 border-yellow-200',
  accepted: 'bg-purple-50 text-purple-700 border-purple-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  settled:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
}
</script>

<template>
  <Head :title="partner.name" />
  <PortalLayout actor-type="organization">
    <div class="space-y-6">
      <!-- Header -->
      <div class="flex items-center gap-3">
        <Link :href="portalOrganizationRootPath()" class="text-muted-foreground hover:text-foreground">
          <ArrowLeft class="size-4" />
        </Link>
        <div class="flex-1">
          <h1 class="text-xl font-semibold">{{ partner.name }}</h1>
          <p class="text-muted-foreground text-sm">Partner</p>
        </div>
        <Badge :variant="partner.active ? 'default' : 'secondary'">
          {{ partner.active ? 'Active' : 'Inactive' }}
        </Badge>
      </div>

      <!-- Details -->
      <Card>
        <CardHeader><CardTitle class="text-base">Partner Details</CardTitle></CardHeader>
        <CardContent>
          <dl class="grid grid-cols-1 gap-y-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt class="text-muted-foreground">Contact Person</dt>
              <dd class="mt-1 font-medium">{{ partner.contact }}</dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Phone</dt>
              <dd class="mt-1">
                <a :href="`tel:${partner.phone}`" class="flex items-center gap-1.5 font-medium hover:underline">
                  <Phone class="size-3.5 shrink-0" />{{ partner.phone }}
                </a>
              </dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Email</dt>
              <dd class="mt-1">
                <a :href="`mailto:${partner.email}`" class="flex items-center gap-1.5 font-medium hover:underline">
                  <Mail class="size-3.5 shrink-0" /><span class="truncate">{{ partner.email }}</span>
                </a>
              </dd>
            </div>
            <div v-if="partner.website">
              <dt class="text-muted-foreground">Website</dt>
              <dd class="mt-1">
                <a :href="partner.website" target="_blank" rel="noopener noreferrer" class="flex items-center gap-1.5 font-medium hover:underline">
                  <ExternalLink class="size-3.5 shrink-0" />{{ partner.website }}
                </a>
              </dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Member Since</dt>
              <dd class="mt-1 font-medium">{{ new Date(partner.created_at).toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' }) }}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <!-- Address -->
      <Card v-if="partner.address">
        <CardHeader><CardTitle class="text-base">Address</CardTitle></CardHeader>
        <CardContent>
          <p class="flex items-start gap-2 text-sm">
            <MapPin class="text-muted-foreground mt-0.5 size-4 shrink-0" />
            {{ formatAddress(partner.address) }}
          </p>
        </CardContent>
      </Card>

      <!-- Applications -->
      <Card>
        <CardHeader><CardTitle class="text-base">Applications</CardTitle></CardHeader>
        <CardContent>
          <div v-if="totalApps === 0" class="text-muted-foreground text-sm">No applications yet.</div>
          <div v-else class="space-y-3">
            <p class="text-sm"><span class="font-semibold">{{ totalApps }}</span> total application{{ totalApps !== 1 ? 's' : '' }}</p>
            <div class="flex flex-wrap gap-2">
              <template v-for="status in APPLICATION_STATUSES" :key="status">
                <div
                  v-if="partner.app_counts[status]"
                  class="flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium"
                  :class="statusColorMap[status]"
                >
                  {{ formatLabel(status) }}
                  <span class="font-semibold">{{ partner.app_counts[status] }}</span>
                </div>
              </template>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  </PortalLayout>
</template>
